import { AgentJobErrorPayload } from "types/api/agentJobs";

// Phase machine shared by panels that submit an agent job and review its result.
// The panel's inputs (text, photo) live outside the phase so they survive
// Try again / Discard. Events that don't apply to the current phase are
// ignored, so a late async result can never move the panel somewhere unexpected.

export type AgentFailure =
  | { kind: "unavailable" }
  | {
      kind: "validation";
      fieldErrors: { field: string; messages: string[] }[];
    }
  | { kind: "generic"; detail: string | null };

export type AgentPhase<TResult> =
  | { name: "input"; error: "requiredAny" | null }
  | { name: "parsing"; jobId: number | null } // jobId is null while the POST is in flight
  | { name: "timeout"; jobId: number }
  | { name: "failed"; failure: AgentFailure }
  | { name: "review"; result: TResult };

export type AgentEvent<TResult> =
  | { type: "SUBMIT"; canSubmit: boolean }
  | { type: "JOB_CREATED"; jobId: number }
  | { type: "JOB_CREATE_FAILED" }
  | { type: "JOB_DONE"; result: TResult }
  | { type: "JOB_FAILED"; failure: AgentFailure }
  | { type: "POLL_TIMED_OUT" }
  | { type: "KEEP_WAITING" }
  | { type: "RETRY" }
  | { type: "DISCARDED" };

export const initialAgentPhase = { name: "input", error: null } as const;

export const reduceAgentPhase = <TResult>(
  phase: AgentPhase<TResult>,
  event: AgentEvent<TResult>,
): AgentPhase<TResult> => {
  switch (event.type) {
    case "SUBMIT":
      // A stuck job never fails on its own; a fresh submit replaces it.
      if (phase.name === "timeout") {
        return event.canSubmit ? { name: "parsing", jobId: null } : phase;
      }
      if (phase.name !== "input") return phase;
      return event.canSubmit
        ? { name: "parsing", jobId: null }
        : { name: "input", error: "requiredAny" };
    case "JOB_CREATED":
      return phase.name === "parsing"
        ? { name: "parsing", jobId: event.jobId }
        : phase;
    case "JOB_CREATE_FAILED":
      return phase.name === "parsing" ? initialAgentPhase : phase;
    case "JOB_DONE":
      return phase.name === "parsing"
        ? { name: "review", result: event.result }
        : phase;
    case "JOB_FAILED":
      return phase.name === "parsing"
        ? { name: "failed", failure: event.failure }
        : phase;
    case "POLL_TIMED_OUT":
      return phase.name === "parsing" && phase.jobId !== null
        ? { name: "timeout", jobId: phase.jobId }
        : phase;
    case "KEEP_WAITING":
      return phase.name === "timeout"
        ? { name: "parsing", jobId: phase.jobId }
        : phase;
    case "RETRY":
      return phase.name === "failed" ? initialAgentPhase : phase;
    case "DISCARDED":
      return phase.name === "review" ? initialAgentPhase : phase;
  }
};

// Rails-side agent misconfiguration or an unreachable agent: the feature is
// down, so panels offer manual entry instead of "Try again".
const UNAVAILABLE_ERRORS = new Set([
  "Agent::ConfigurationError",
  "Agent::ConnectionError",
  "agent_unavailable",
]);

// Backend error_payload → what a panel shows. validation_failed carries model
// errors per field in `details`; anything else is shown as its error code.
export const describeAgentFailure = (
  payload: AgentJobErrorPayload | null,
): AgentFailure => {
  if (!payload) return { kind: "generic", detail: null };
  if (UNAVAILABLE_ERRORS.has(payload.error)) return { kind: "unavailable" };
  if (payload.error === "validation_failed") {
    const fieldErrors = Object.entries(payload.details ?? {}).map(
      ([field, messages]) => ({ field, messages }),
    );
    if (fieldErrors.length > 0) return { kind: "validation", fieldErrors };
  }
  return { kind: "generic", detail: payload.message ?? payload.error ?? null };
};
